import { createClient, User, Session } from '@supabase/supabase-js';

// Environment validation with type safety
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables at runtime
const validateEnv = () => {
  const errors: string[] = [];

  if (!supabaseUrl) {
    errors.push('VITE_SUPABASE_URL is required');
  } else {
    try {
      new URL(supabaseUrl);
    } catch {
      errors.push('VITE_SUPABASE_URL must be a valid URL');
    }
  }

  if (!supabaseAnonKey) {
    errors.push('VITE_SUPABASE_ANON_KEY is required');
  } else if (!supabaseAnonKey.startsWith('eyJ')) {
    errors.push('VITE_SUPABASE_ANON_KEY must be a valid JWT');
  }

  if (errors.length > 0) {
    throw new Error(`Missing or invalid environment variables:\n${errors.join('\n')}`);
  }
};

// Only validate in development, allow graceful fallback in production builds
if (import.meta.env.DEV || !import.meta.env.PROD) {
  validateEnv();
}

export const supabase = createClient(
  supabaseUrl!,
  supabaseAnonKey!,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

// Re-export onAuthStateChange from supabase
export const onAuthStateChange = (callback: (event: string, session: any) => void) =>
  supabase.auth.onAuthStateChange(callback);

// Auth helper functions
export const auth = {
  signUp: (email: string, password: string, options?: { data?: object; options?: object }) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: options?.data,
        ...options?.options,
      }
    }),

  signIn: (email: string, password: string) =>
    supabase.auth.signInWithPassword({ email, password }),

  signInWithOAuth: (provider: 'google' | 'github', options?: { options?: Record<string, any> }) =>
    supabase.auth.signInWithOAuth({
      provider,
      ...options?.options,
    }),

  signOut: () => supabase.auth.signOut(),

  getSession: () => supabase.auth.getSession(),

  getUser: () => supabase.auth.getUser(),

  onAuthStateChange: (callback: (event: string, session: any) => void) =>
    supabase.auth.onAuthStateChange(callback),

  resetPassword: (email: string, options?: { redirectTo?: string }) =>
    supabase.auth.resetPasswordForEmail(email, options),

  // ⚠️ CRITICAL: Refresh session to fix expired auth tokens
  refreshSession: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('Session refresh failed:', error.message);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Unknown error';
      console.warn('Session refresh error:', error);
      return { success: false, error };
    }
  },
};

// Edge Function invoke helper with automatic session refresh on auth errors
export async function invokeEdgeFunction<T>(
  functionName: string,
  body: object,
  options?: { maxRetries?: number }
): Promise<{ data: T | null; error: { message: string; isAuthError?: boolean; shouldReauth?: boolean } | null }> {
  const maxRetries = options?.maxRetries ?? 1;
  let lastError: any = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
      });

      if (error) {
        // Get error message safely - handle various error formats
        const errorMessage = typeof error === 'string' ? error :
          error?.message || error?.error?.message || JSON.stringify(error);

        // Check if this is an auth error (401/403)
        const isAuthError =
          errorMessage.includes('401') ||
          errorMessage.includes('403') ||
          errorMessage.includes('JWT') ||
          errorMessage.includes('session') ||
          errorMessage.includes('auth') ||
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Invalid token') ||
          errorMessage.includes('Missing authorization');

        console.log(`Edge Function error:`, error, `isAuthError: ${isAuthError}`);

        // If auth error and we haven't retried yet, try refreshing session
        if (isAuthError && attempt < maxRetries) {
          console.warn(`Auth error on attempt ${attempt + 1}, refreshing session...`);
          const refreshResult = await auth.refreshSession();
          if (refreshResult.success) {
            console.log('Session refreshed, retrying request...');
            continue; // Retry the request
          } else {
            console.warn('Session refresh failed - user must re-authenticate');
            // If refresh fails, user needs to log in again - mark for re-auth
            return {
              data: null,
              error: {
                message: 'Session expired. Please log in again.',
                isAuthError: true,
                shouldReauth: true
              }
            };
          }
        }

        return {
          data: null,
          error: {
            message: errorMessage || 'Edge Function error',
            isAuthError
          }
        };
      }

      return { data, error: null };
    } catch (err) {
      lastError = err;
    }
  }

  return {
    data: null,
    error: {
      message: lastError?.message || 'Unknown error',
      isAuthError: false
    }
  };
}

export default supabase;
